"use client"

import { useState } from "react"
import { cn } from "@/lib/utils"
import { PersonIcon } from "@/components/ui/icons"

function initialsFrom(name: string | undefined): string {
  const words = (name ?? "").trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return ""
  return words
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("")
}

/**
 * The avatar shown beside a human teammate's chat replies — their Chatwoot
 * profile photo, falling back to an initials monogram when they haven't set one
 * (Chatwoot sends `avatar_url: ""` in that case) or the image fails to load.
 * Ella (the AI) and the visitor never get one, so a face appearing here is the
 * signal that a real person took over from the bot.
 *
 * Decorative: the sender is already named in the attribution line beside it, so
 * it's `aria-hidden` to avoid announcing the same person twice.
 */
export function AgentAvatar({
  avatarUrl,
  name,
  className,
}: {
  avatarUrl?: string
  name?: string
  className?: string
}) {
  const [broken, setBroken] = useState(false)
  const src = avatarUrl?.trim()
  const showPhoto = Boolean(src) && !broken
  const initials = initialsFrom(name)

  return (
    <span
      aria-hidden="true"
      className={cn(
        // ring-inset: the avatar can sit flush at a message-scroller item's
        // left edge, and `[content-visibility:auto]` on the item paint-clips at
        // its border box — an outset ring loses its leftmost pixel there.
        "ring-border flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-neutral-100 ring-1 ring-inset",
        className
      )}
    >
      {showPhoto ? (
        // eslint-disable-next-line @next/next/no-img-element -- runtime Chatwoot avatar URL, not a static asset; next/image only proxies a self-hosted, non-CDN host here
        <img
          src={src}
          alt=""
          loading="lazy"
          className="h-full w-full object-cover"
          onError={() => setBroken(true)}
        />
      ) : initials ? (
        <span className="type-label-sm font-semibold text-neutral-600 normal-case">
          {initials}
        </span>
      ) : (
        // No photo and no name to derive initials from — a generic teammate
        // glyph beats an empty circle.
        <PersonIcon width={16} height={16} className="text-neutral-500" />
      )}
    </span>
  )
}
